import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "react-query";
import { fetchItem, postItem } from "../queries/queries-helper";
import ENDPOINTS from "../services/endpoints";
import ResponseApi from "../models/interfaces/response-api";
import { JobCreatedDto } from "../models/interfaces/dtos/production/job-created-dto";
import JobDto, { prodTaskAdapter } from "../models/interfaces/dtos/production/job-dto";
import { UnifiedJobStatus } from "../models/interfaces/dtos/job";
import useProjectContext from "../services/context/project/use-project-context";
import useProjectProcessContext from "../services/context/project-process/use-project-process-context";
import pollJob from "./jobs/poll-job";

const useProjectAccess = (projectId: string) => {
    const { project } = useProjectContext();
    const { beginProcess } = useProjectProcessContext();
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const currentRevision = project.baamerRevisionId;
    const accessQueryResult = useQuery(
        ["project-access", projectId],
        async () => fetchItem<boolean>(ENDPOINTS.BOOMER_PROJECT_CAN_POST(projectId)),
    );
    const baamerRevisionQueryResult = useQuery(
        ["baamer-revision-post", currentRevision],
        async () => fetchItem<boolean>(ENDPOINTS.BOOMER_BAAMER_REVISION_POSTED(currentRevision || "")),
        { enabled: accessQueryResult.data === true },
    );

    // Les mutations de lancement attendent la fin du job, comme les hooks de jobs.
    const runTrackedJob = async (url: string, statusUrl: (jobId: string) => string, data?: unknown) => {
        const finishProcess = beginProcess();
        try {
            const response = await postItem<ResponseApi<JobCreatedDto>>(url, data);
            if (!response?.success) throw new Error(response?.errorMessages?.[0] ?? "Le lancement du job a échoué.");
            const status = await pollJob(
                () => fetchItem<JobDto>(statusUrl(response.entity.jobId)),
                prodTaskAdapter.getStatus,
                () => isMounted.current,
            );
            if (!status) throw new Error("Le suivi du job a été interrompu.");
            if (prodTaskAdapter.getStatus(status) !== UnifiedJobStatus.Success) throw new Error("Le job a échoué.");
            return response;
        } finally {
            finishProcess();
        }
    };

    const launchNewRevision = useMutation({
        mutationFn: () => runTrackedJob(ENDPOINTS.REVISION_LAUNCH_NEW(projectId), ENDPOINTS.REVISION_GET_NEW_REVISION_STATUS),
    });
    const getLaunchNewRevisionStatus = useMutation({
        mutationFn: (jobId: string) => fetchItem<JobDto>(ENDPOINTS.REVISION_GET_NEW_REVISION_STATUS(jobId)),
    });
    const launchProcess = useMutation({
        mutationFn: (processName: string) => runTrackedJob(ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, processName, false), ENDPOINTS.PRODUCTION_GET_JOB_STATUS),
    });
    const getLaunchProcessStatus = useMutation({
        mutationFn: (jobId: string) => fetchItem<JobDto>(ENDPOINTS.PRODUCTION_GET_JOB_STATUS(jobId)),
    });
    const launchPostRevision = useMutation({
        mutationFn: (newBoomerRevision: boolean) => runTrackedJob(
            ENDPOINTS.BOOMER_LAUNCH_POST_REVISION(projectId),
            ENDPOINTS.BOOMER_GET_POST_REVISION_STATUS,
            { projectBoomerId: project.projectBoomerId, newBoomerRevision },
        ),
    });
    const getPostRevisionStatus = useMutation({
        mutationFn: (jobId: string) => fetchItem<JobDto>(ENDPOINTS.BOOMER_GET_POST_REVISION_STATUS(jobId)),
    });

    return {
        accessQueryResult,
        baamerRevisionQueryResult,
        launchNewRevision,
        getLaunchNewRevisionStatus,
        launchProcess,
        getLaunchProcessStatus,
        launchPostRevision,
        getPostRevisionStatus,
    };
};

export default useProjectAccess;
